import { z } from "zod"

export const enum DesignRequestType {
  RealTime = `realtime`,
  OverNight = `over-night`,
  SelfRequest = `self-request`,
}

export enum RoofMaterialEnum {
  Concrete = `concrete`,
  Glass = `glass`,
  Metal = `metal`,
  Shingles = `shingles`,
  Thatch = `thatch`,
  Wood = `wood`,
}

export const powurAddressSchema = z.object({
  street: z.string(),
  city: z.string(),
  state: z.string(),
  postal: z.string(),
  category: z.string(),
  lat: z.number(),
  long: z.number(),
})
export type PowurAddress = z.infer<typeof powurAddressSchema>
export function isPartialPowurAddress(
  obj: unknown,
): obj is Partial<PowurAddress> {
  return (
    typeof obj === `object` &&
    obj !== null &&
    `street` in obj &&
    (`city` in obj ||
      `state` in obj ||
      `postal` in obj ||
      `category` in obj ||
      `lat` in obj ||
      `long` in obj) &&
    !(`postalCost` in obj) &&
    !(`locality` in obj)
  )
}

export const powurRoofSchema = z.object({
  age: z.string(),
  type: z.string(),
  main_service_panel: z.string(),
})
export type PowurRoof = z.infer<typeof powurRoofSchema>

export const powurConstructionDetailsSchema = z.object({
  preference: z.string(),
  other: z.array(z.string()),
  secondary_system: z.boolean().optional(),
  new_construction: z.boolean().optional(),
})
export type PowurConstructionDetails = z.infer<
  typeof powurConstructionDetailsSchema
>

export const powurEnterpriseMarginSettingsSchema = z
  .object({
    margin_fixed: z.boolean(),
    proposal_margin: z.number(),
    manager_margin_adjustment: z.boolean(),
    manager_minimum_margin: z.number(),
  })
  .nullable()
export type PowurEnterpriseMarginSettings = z.infer<
  typeof powurEnterpriseMarginSettingsSchema
>

export type IPowurEnterpriseMarginSettingsDto = {
  margin_fixed: boolean
  proposal_margin: number
  manager_margin_adjustment: boolean
  manager_minimum_margin: number
}

export const brochureSlidesSchema = z.object({
  name: z.string(),
  url: z.string().optional(),
})
export type BrochureSlides = z.infer<typeof brochureSlidesSchema>

export const platformEnterpriseCobrandSettingsSchema = z
  .object({
    enterprise_id: z.number(),
    brochure_slides: z.array(brochureSlidesSchema),
  })
  .nullable()
export type PlatformEnterpriseCobrandSettings = z.infer<
  typeof platformEnterpriseCobrandSettingsSchema
>

export type IPlatformEnterpriseCobrandSettingsDto = {
  enterprise_id: number
  brochure_slides: BrochureSlides[]
}

export const powurHoaSchema = z.object({
  name: z.string(),
  phone: z.string(),
})
export type PowurHoa = z.infer<typeof powurHoaSchema>

export const powurImageSizeSchema = z.object({
  large: z.string().nullable().optional(),
  thumb: z.string().nullable().optional(),
  retina: z.string().nullable().optional(),
  preview: z.string().nullable().optional(),
  original: z.string().nullable().optional(),
})
export type PowurImageSize = z.infer<typeof powurImageSizeSchema>

export const powurProfileImgSchema = z.object({
  bucket: z.string().nullable(),
  sizes: powurImageSizeSchema.nullable(),
})
export type PowurProfileImg = z.infer<typeof powurProfileImgSchema>

export const powurUserLicenseSchema = z.object({
  id: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
  state: z.string(),
  name: z.string(),
  identifier: z.string(),
  expires_at: z.date(),
  userId: z.string().uuid(),
})
export type PowurUserLicense = z.infer<typeof powurUserLicenseSchema>

export const powurPutUserSchema = z.object({
  id: z.number(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
  phone: z.string().optional(),
  profile_img: powurProfileImgSchema.optional(),
  bio: z.string().optional(),
  permissions: z.array(z.string()).optional(),
  licenses: z.array(powurUserLicenseSchema.optional()).optional(),
})
export type PowurPutUser = z.infer<typeof powurPutUserSchema>

export const powurUserSchema = powurPutUserSchema.extend({
  mentor: powurPutUserSchema.optional(),
})
export type PowurUser = z.infer<typeof powurUserSchema>

export const powurEnterpriseLogoSchema = z.object({
  bucket: z.string(),
  key: z.string(),
})
export type PowurEnterpriseLogo = z.infer<typeof powurEnterpriseLogoSchema>

export const powurEnterpriseSchema = z.object({
  id: z.number(),
  name: z.string(),
  logo: powurEnterpriseLogoSchema.optional(),
  owner: powurPutUserSchema,
  margin_settings: powurEnterpriseMarginSettingsSchema,
  should_cobrand: z.boolean().optional().nullable(),
  margin_visibility: z.boolean().optional(),
  commission_visibility: z.boolean().optional(),
  cobrand_settings: platformEnterpriseCobrandSettingsSchema
    .optional()
    .nullable(),
})
export type PowurEnterprise = z.infer<typeof powurEnterpriseSchema>

export const powurProposalSettingsSchema = z.object({
  primary_seller: z.nullable(z.number()),
  secondary_seller: z.nullable(z.number()),
})
export type PowurProposalSettings = z.infer<typeof powurProposalSettingsSchema>

export const powurProjectInfoSchema = z.object({
  id: z.number(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
  phone: z.string(),
  address: powurAddressSchema,
  lead_source: z.string().nullable().optional(),
})
export type PowurProjectInfo = z.infer<typeof powurProjectInfoSchema>

export const existingSystemOwnerSchema = z.enum([
  `owned_outright`,
  `active_loan`,
  `tpo`,
])

export const powurExistingSystemSchema = z.object({
  ownership: existingSystemOwnerSchema.nullable().optional(),
})
export type PowurExistingSystem = z.infer<typeof powurExistingSystemSchema>

export const powurSiteInfoSchema = z.object({
  installer: z.string(),
  roof: powurRoofSchema,
  construction_details: powurConstructionDetailsSchema,
  hoa: powurHoaSchema,
  financing: z.string().optional(),
  proposal_notes: z.string().optional(),
  project_notes: z.string().optional(),
  existing_system: powurExistingSystemSchema.nullable().optional(),
})
export type PowurSiteInfo = z.infer<typeof powurSiteInfoSchema>

export const powurUtilityBillInfoSchema = z.object({
  url: z.string().optional(),
  company: z.string(),
  annual_consumption_kwh: z.number().nullable(),
  avg_monthly_bill: z.number().nullable(),
})
export type PowurUtilityBillInfo = z.infer<typeof powurUtilityBillInfoSchema>

export const powurProjectSchema = z.object({
  project_info: powurProjectInfoSchema,
  site_info: powurSiteInfoSchema,
  utility_bill_info: powurUtilityBillInfoSchema,
  seller: powurUserSchema,
  enterprise: z.nullable(powurEnterpriseSchema),
  proposal_settings: z.nullable(powurProposalSettingsSchema),
})
export type PowurProject = z.infer<typeof powurProjectSchema>

export const powurCreateProposalSchema = z.object({
  project: powurProjectSchema,
  request_type: z.nullable(
    z.union([
      z.literal(DesignRequestType.RealTime),
      z.literal(DesignRequestType.OverNight),
      z.literal(DesignRequestType.SelfRequest),
    ]),
  ),
  metadata: z.nullable(z.string()),
})
export type PowurCreateProposal = z.infer<typeof powurCreateProposalSchema>
